import React from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Task #3203 — Shell hub condivisa (header inline icona+titolo+sottotitolo
 * + tab bar sticky). Unica fonte del markup per Catalogo, Magazzino
 * (LogisticsHub) e Amministrazione: evita che le tre pagine divergano.
 */

export interface HubTab<K extends string> {
  key: K;
  label: string;
  icon: LucideIcon;
  /** Label compatta mostrata sotto il breakpoint lg. Default: prima parola della label. */
  mobileLabel?: string;
  /** Badge numerico opzionale (es. contatore fatture non lette). 0 → nascosto. */
  badge?: number;
}

export interface HubShellProps<K extends string> {
  icon: LucideIcon;
  title: string;
  /** Sottotitolo breve mostrato accanto al titolo (nascosto su mobile). */
  subtitle?: string;
  /** Slot azioni a destra nell'header (es. bottone refresh, dialog "Nuova ..."). */
  headerActions?: React.ReactNode;
  /** Nodo extra inline dopo il sottotitolo (es. badge modalità Calendario). */
  headerExtra?: React.ReactNode;
  tabs: readonly HubTab<K>[];
  activeTab: K;
  onTabChange: (key: K) => void;
  /** Classi extra sul wrapper esterno (es. sfondo pagina). */
  className?: string;
  /** Classi del contenitore body. Default: 'flex-1 overflow-auto p-4'. */
  mainClassName?: string;
  /**
   * Task #3486 — opt-in: spazio verticale simmetrico nell'header (py-4 invece
   * di pt-4 pb-3), per dare lo stesso respiro sopra e sotto la fascia
   * titolo/tab. Default false per non toccare gli altri consumer.
   */
  evenHeaderSpacing?: boolean;
  /**
   * Default true: shell full-height (flex-col h-full) con body scrollabile.
   * false per pagine a scroll di pagina (es. Calendario, CRM): niente h-full,
   * il body non è un contenitore overflow.
   */
  fullHeight?: boolean;
  children: React.ReactNode;
}

export function HubShell<K extends string>({
  icon: HeaderIcon,
  title,
  subtitle,
  headerActions,
  headerExtra,
  tabs,
  activeTab,
  onTabChange,
  className,
  mainClassName,
  evenHeaderSpacing = false,
  fullHeight = true,
  children,
}: HubShellProps<K>) {
  return (
    <div className={`${fullHeight ? 'flex flex-col h-full' : 'flex flex-col'} ${className ?? ''}`}>
      {/* Header — icona + titolo + sottotitolo + azioni. */}
      <div className={`px-4 ${evenHeaderSpacing ? 'py-4' : 'pt-4 pb-3'} bg-white dark:bg-slate-900`}>
        <div className="flex items-center gap-2 flex-wrap">
          <HeaderIcon className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h1>
          {subtitle && (
            <span className="text-xs text-muted-foreground hidden sm:inline">— {subtitle}</span>
          )}
          {headerExtra}
          {headerActions && <div className="ml-auto flex items-center gap-1">{headerActions}</div>}
        </div>
      </div>

      {/* Menu orizzontale sticky — border-b-2 indigo per active, label compatta su mobile. */}
      <div className="border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="overflow-x-auto scrollbar-hide">
          <nav className="flex w-full" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => onTabChange(tab.key)}
                  className={`
                    flex-1 flex items-center justify-center gap-1.5 px-1 py-2.5 text-xs font-medium whitespace-nowrap
                    border-b-2 transition-colors duration-150
                    ${isActive
                      ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-300'
                      : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-700'
                    }
                  `}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="hidden lg:inline">{tab.label}</span>
                  <span className="lg:hidden">{tab.mobileLabel ?? tab.label.split(' ')[0]}</span>
                  {typeof tab.badge === 'number' && tab.badge > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Body — sub-pagina embeddata full-width. */}
      <main className={mainClassName ?? (fullHeight ? 'flex-1 overflow-auto p-4' : '')}>
        {children}
      </main>
    </div>
  );
}
